import React, {useCallback, useEffect, useRef} from 'react';
import {Editor, ExposedEditorData} from './Editor/Editor';
import {noop} from '@priority-software/priority-style-react';
import {formsStore} from '../../../../stores/Form/FormsStore';
import {Form} from '../../../../stores/Form/Form';
import {eventPrevented} from '../../../../utils/eventPrevented';
import {runSiblingSubform} from '../../utils/grid-methods';
import {subFormNavDirections} from '../../consts/grid';
import {keys} from '../../../../constants/KeyCodes';
import {checkAndSaveHtml} from '../../actions/form';
import {observer} from 'mobx-react';
import styles from './styles.module.scss';

type Props = {
    form: Form;
    readonly?: boolean;
    isApiLock: () => boolean;
};

export const HTMLForm = observer(({form, readonly, isApiLock}: Props) => {
    const {level, HTMLContent} = form;
    const parentLevel = form.parentForm?.level;
    const isActive = formsStore.getActiveForm() === form;

    const editorRef = useRef<ExposedEditorData>();

    const onSave = useCallback(async () => {
        await checkAndSaveHtml(form);
    }, [form]);

    const preserveHTMLState = useCallback(
        (html: string) => {
            // save HTML state when user switch between the screens
            form.setHTMLContent(html);
        },
        [form]
    );

    const onFocus = useCallback(async () => {
        const editorAPI = editorRef.current?.editor.current;
        if (editorAPI) {
            await formsStore.currentScreen.onFieldFocusChange(
                {
                    level,
                    newCellIndex: undefined,
                    newRowIndex: undefined
                },
                noop
            );
        }
    }, [level]);

    useEffect(() => {
        if (isActive) {
            const editorInstance = editorRef.current?.editor?.current;
            if (editorRef.current) {
                form.setEditorInstance(editorInstance);
            }
            const isSubForm = !!form.parentForm;

            const onKeyDown = async (e: KeyboardEvent) => {
                if (!isApiLock()) {
                    if (e.code === keys.ESC) {
                        e.preventDefault();
                        await formsStore.currentScreen.handleFormLevel(parentLevel);
                    } else if (e.code === keys.ARROW_LEFT) {
                        if (e.altKey && isSubForm) {
                            eventPrevented(e, true);
                            await runSiblingSubform(subFormNavDirections.left, form);
                        }
                    } else if (e.code === keys.ARROW_RIGHT) {
                        if (e.altKey && isSubForm) {
                            eventPrevented(e, true);
                            await runSiblingSubform(subFormNavDirections.right, form);
                        }
                    }
                }
            };

            window.addEventListener('keydown', onKeyDown);
            return () => window.removeEventListener('keydown', onKeyDown);
        }
        // clear editor ref in the form store;
        form.setEditorInstance(null);
        return noop;
    }, [parentLevel, isActive, form, isApiLock]);

    const handleClick = async () => {
        await formsStore.currentScreen.handleFormLevel(form.level);
    };

    const content =
        readonly || !isActive ? (
            <div
                onClick={handleClick}
                className={styles.readOnlyContent}
                dangerouslySetInnerHTML={{__html: HTMLContent}}
            />
        ) : (
            <Editor
                ref={editorRef}
                namespace="HTML-form-editor"
                value={HTMLContent}
                isReadonly={false}
                onSave={onSave}
                onFocus={onFocus}
                onUnmount={preserveHTMLState}
            />
        );

    return <div className={styles.component}>{content}</div>;
});
